import { useCallback } from "react";

import { useRouter } from "next/router";

import {
  PRICE_ID_TO_INTERVAL_MAP,
  PRICE_ID_TO_PLAN_MAP,
  type TPriceId,
} from "@musicfy/contants/Subscriptions";
import { useAnalytics } from "@musicfy/libs/AnalyticsProvider";
import { usePaddleContext } from "@musicfy/libs/PaddleProvider";
import { PaddleScreens } from "@musicfy/libs/PaddleProvider/types";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { useToastContext } from "@musicfy/libs/ToastProvider";
import { api } from "@musicfy/utils/api";
import {
  EXPIRED_CHECKOUT_PROMO_MESSAGE,
  clearCheckoutPromoCode,
} from "@musicfy/utils/checkoutPromo";

const emptyStripeCheckout = {
  clientSecret: null,
  sessionId: null,
  subscriptionId: null,
  promoApplied: false,
  amountTotal: null,
  amountDiscount: 0,
  currency: "usd",
  email: "",
} as const;

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
};

export const useStripeCheckout = () => {
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const { sendToastMessage } = useToastContext();
  const { setSubscription } = useSubscriptionContext();
  const { stripeCheckoutContext, setActivePaddleScreen, origin, setOrigin } =
    usePaddleContext();

  const {
    stripeCheckout,
    setStripeCheckout,
    isStripeCheckoutUpdating,
    setIsStripeCheckoutUpdating,
    isStripePaymentProcessing,
    setIsStripePaymentProcessing,
    canCloseStripeCheckout,
    setCanCloseStripeCheckout,
    isFetchingStripeSubscriptionRef,
  } = stripeCheckoutContext;

  const createStripeCheckoutSession =
    api.subscription.createStripeCheckoutSession.useMutation();
  const syncStripeConsumerSubscription =
    api.subscription.syncStripeConsumerSubscription.useMutation();
  const cancelIncompleteStripeCheckout =
    api.subscription.cancelIncompleteStripeCheckout.useMutation();

  const startStripeCheckout = useCallback(
    async ({
      priceId,
      promoCode,
      origin: checkoutOrigin,
    }: {
      priceId: TPriceId;
      promoCode?: string;
      origin?: string;
    }) => {
      const resolvedOrigin = checkoutOrigin || origin;
      if (checkoutOrigin) {
        setOrigin(checkoutOrigin);
      }

      setCanCloseStripeCheckout(true);
      setIsStripePaymentProcessing(false);
      isFetchingStripeSubscriptionRef.current = false;
      setActivePaddleScreen(PaddleScreens.STRIPE_CHECKOUT);
      setStripeCheckout({
        priceId,
        promoCode,
        ...emptyStripeCheckout,
      });

      try {
        const session = await createStripeCheckoutSession.mutateAsync({
          priceId,
          origin: resolvedOrigin,
          promoCode,
        });

        if (promoCode && !session.promoApplied) {
          sendToastMessage(EXPIRED_CHECKOUT_PROMO_MESSAGE, {
            severity: "warning",
          });
        }
        clearCheckoutPromoCode();

        const appliedPromo = session.promoApplied ? promoCode : undefined;
        setStripeCheckout({
          priceId,
          clientSecret: session.clientSecret,
          sessionId: session.sessionId,
          subscriptionId: null,
          promoCode: appliedPromo,
          promoApplied: session.promoApplied,
          amountTotal: session.amountTotal,
          amountDiscount: session.amountDiscount,
          currency: session.currency ?? "usd",
          email: session.email,
        });

        trackEvent("Checkout Started", {
          plan: PRICE_ID_TO_PLAN_MAP[priceId],
          interval: PRICE_ID_TO_INTERVAL_MAP[priceId],
          origin: resolvedOrigin,
          checkout_provider: "stripe",
          ...(appliedPromo ? { promo_code: appliedPromo } : {}),
        });

        return { promoApplied: session.promoApplied };
      } catch (error) {
        setStripeCheckout(null);
        setActivePaddleScreen(null);
        throw error;
      }
    },
    [
      createStripeCheckoutSession,
      origin,
      sendToastMessage,
      setActivePaddleScreen,
      setCanCloseStripeCheckout,
      setIsStripePaymentProcessing,
      setOrigin,
      setStripeCheckout,
      trackEvent,
      isFetchingStripeSubscriptionRef,
    ]
  );

  const recreateSession = useCallback(
    async (promoCode?: string) => {
      if (!stripeCheckout) {
        return { success: false as const };
      }

      setIsStripeCheckoutUpdating(true);
      try {
        const session = await createStripeCheckoutSession.mutateAsync({
          priceId: stripeCheckout.priceId,
          origin,
          promoCode,
        });

        if (promoCode && !session.promoApplied) {
          return { success: false as const, promoApplied: false };
        }

        setStripeCheckout({
          priceId: stripeCheckout.priceId,
          clientSecret: session.clientSecret,
          sessionId: session.sessionId,
          subscriptionId: null,
          promoCode: session.promoApplied ? promoCode : undefined,
          promoApplied: session.promoApplied,
          amountTotal: session.amountTotal,
          amountDiscount: session.amountDiscount,
          currency: session.currency ?? "usd",
          email: session.email || stripeCheckout.email,
        });

        return {
          success: session.promoApplied || !promoCode,
          promoApplied: session.promoApplied,
        };
      } catch (error) {
        sendToastMessage(
          getErrorMessage(
            error,
            "Unable to update checkout. Please try again."
          ),
          { severity: "error" }
        );
        return { success: false as const, promoApplied: false };
      } finally {
        setIsStripeCheckoutUpdating(false);
      }
    },
    [
      createStripeCheckoutSession,
      origin,
      sendToastMessage,
      setIsStripeCheckoutUpdating,
      setStripeCheckout,
      stripeCheckout,
    ]
  );

  const applyPromoCode = useCallback(
    async (promoCode: string) => {
      const result = await recreateSession(promoCode);
      if (!result.success || !result.promoApplied) {
        return { success: false };
      }
      return { success: true };
    },
    [recreateSession]
  );

  const removePromoCode = useCallback(async () => {
    await recreateSession(undefined);
  }, [recreateSession]);

  const cancelIncompleteStripeSubscription = useCallback(
    async (subscriptionId: string) => {
      try {
        await cancelIncompleteStripeCheckout.mutateAsync({ subscriptionId });
      } catch (error) {
        console.error("Unable to cancel incomplete Stripe Checkout", error);
      }
    },
    [cancelIncompleteStripeCheckout]
  );

  const closeStripeCheckout = useCallback(() => {
    if (isStripePaymentProcessing || !canCloseStripeCheckout) {
      return;
    }

    const incompleteSubscriptionId = stripeCheckout?.subscriptionId;
    if (incompleteSubscriptionId) {
      void cancelIncompleteStripeSubscription(incompleteSubscriptionId);
    }

    setOrigin(undefined);
    setStripeCheckout(null);
    setActivePaddleScreen(null);
  }, [
    cancelIncompleteStripeSubscription,
    canCloseStripeCheckout,
    isStripePaymentProcessing,
    setActivePaddleScreen,
    setOrigin,
    setStripeCheckout,
    stripeCheckout?.subscriptionId,
  ]);

  const completeStripeCheckout = useCallback(
    async (subscriptionIdOverride?: string) => {
      const subscriptionId =
        subscriptionIdOverride ?? stripeCheckout?.subscriptionId;
      const priceId = stripeCheckout?.priceId;
      if (!subscriptionId || !priceId) {
        return;
      }

      setIsStripePaymentProcessing(true);
      setCanCloseStripeCheckout(false);

      if (!isFetchingStripeSubscriptionRef.current) {
        isFetchingStripeSubscriptionRef.current = true;
        const plan = PRICE_ID_TO_PLAN_MAP[priceId];
        const interval = PRICE_ID_TO_INTERVAL_MAP[priceId];
        trackEvent("Subscription Upgrade", {
          plan,
          interval,
          origin,
          checkout_provider: "stripe",
        });
      }

      try {
        const syncedSubscription =
          await syncStripeConsumerSubscription.mutateAsync({
            subscriptionId,
          });
        setSubscription(syncedSubscription);
      } catch (error) {
        console.error("Unable to sync completed Stripe Checkout", error);
      }

      window.setTimeout(() => {
        router.reload();
      }, 4000);
    },
    [
      origin,
      router,
      setCanCloseStripeCheckout,
      setIsStripePaymentProcessing,
      setSubscription,
      stripeCheckout?.priceId,
      stripeCheckout?.subscriptionId,
      syncStripeConsumerSubscription,
      trackEvent,
      isFetchingStripeSubscriptionRef,
    ]
  );

  return {
    startStripeCheckout,
    closeStripeCheckout,
    cancelIncompleteStripeSubscription,
    completeStripeCheckout,
    applyPromoCode,
    removePromoCode,
    isStripeCheckoutUpdating,
    isStartingCheckout: createStripeCheckoutSession.isLoading,
  };
};
